/**
 * ホームページ
 *
 * ログイン後のダッシュボード画面
 * よく使う機能へのショートカットを提供
 */

import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useApi } from "../hooks/useApi";
import type { ExamSession } from "../types";

interface RecentExam extends ExamSession {
  patient?: { id: string; name: string; patientCode: string };
  examiner?: { id: string; name: string };
}

function HomePage() {
  const { user } = useAuth();
  const isSystemAdmin = user?.role === "SYSTEM_ADMIN";

  // システム管理者はホームを表示せず施設一覧へリダイレクトする
  if (isSystemAdmin) {
    return <Navigate to="/facilities" replace />;
  }

  // 最近の検査を取得（SYSTEM_ADMIN以外）
  const { data: recentExams, loading } = useApi<RecentExam[]>(
    isSystemAdmin ? "" : "/api/exams/recent",
    [isSystemAdmin],
  );

  // メニューカード（ロールに応じて表示）
  const menuCards = [
    ...(!isSystemAdmin
      ? [
          {
            title: "新規検査",
            description: "線分二等分課題を実施",
            path: "/exam/settings",
            icon: "📝",
            color: "bg-green-500",
          },
        ]
      : []),
  ];

  return (
    <div>
      {/* ページタイトル */}
      <h1 className="text-2xl font-bold text-gray-800 mb-6">ダッシュボード</h1>

      {/* メニューカード */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {menuCards.map((card) => (
          <Link
            key={card.path}
            to={card.path}
            className="block bg-white rounded-lg shadow hover:shadow-md transition-shadow"
          >
            <div className={`${card.color} h-2 rounded-t-lg`} />
            <div className="p-6">
              <div className="text-3xl mb-2">{card.icon}</div>
              <h2 className="text-lg font-semibold text-gray-800 mb-1">
                {card.title}
              </h2>
              <p className="text-sm text-gray-500">{card.description}</p>
            </div>
          </Link>
        ))}
      </div>

      {/* 最近の検査（SYSTEM_ADMIN以外） */}
      {!isSystemAdmin && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            最近の検査
          </h2>
          <div className="bg-white rounded-lg shadow">
            {loading ? (
              <div className="p-6 text-center text-gray-500">読み込み中...</div>
            ) : !recentExams || recentExams.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                検査履歴はまだありません
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {recentExams.slice(0, 5).map((exam) => (
                  <div key={exam.id} className="p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium text-gray-900">
                          {exam.patient?.name || "不明"}
                          <span className="ml-2 text-sm text-gray-500">
                            ({exam.patient?.patientCode})
                          </span>
                        </p>
                        <p className="text-sm text-gray-500">
                          {new Date(exam.createdAt).toLocaleString("ja-JP")}
                          {" / "}試行: {exam.totalTrials}回
                          {exam.averageDeviationMm !== null && (
                            <>
                              {" / "}平均偏差:{" "}
                              {Number(exam.averageDeviationMm).toFixed(2)}mm
                            </>
                          )}
                        </p>
                        {exam.deviceInfo &&(
                          <p className="text-xs text-gray-400 mt-0.5">
                            端末:{" "}
                            {String((exam.deviceInfo as Record<string, unknown>).platform || "-")}
                            {" / "}
                            {String((exam.deviceInfo as Record<string, unknown>).screenWidth || "-")}
                            x
                            {String((exam.deviceInfo as Record<string, unknown>).screenHeight || "-")}
                            {" / "}DPR:{" "}
                            {String((exam.deviceInfo as Record<string, unknown>).devicePixelRatio || "-")}
                          </p>
			  )}
                      </div>
                      {exam.patient && (
                        <Link
                          to={`/patients/${exam.patient.id}`}
                          className="px-2 py-1 rounded border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-800 transition-colors"
                        >
                          詳細
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default HomePage;
